import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { Store } from '@ngrx/store';
import { BehaviorSubject, firstValueFrom, map, Observable } from 'rxjs';

import { ActionIcons } from '../../../ui/app-actions.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import {
	deleteProject,
	ProjectSelector,
	sendVerificationCode,
} from '../../store';
import { ProjectItemEntity } from '../project-list/project-item.entity';

export enum ProjectDeleteStep {
	ProjectElements = 0,
	VerificationCode = 1,
}

export enum ProjectDeletePopupMode {
	UndeletableMode = 'undeletable',
	UncriticalMode = 'uncritical',
	CriticalMode = 'critical',
}

export interface IProjectDeletePopupData {
	project: ProjectItemEntity;
}

@Component({
	selector: 'app-project-delete-popup',
	templateUrl: './project-delete-popup.component.html',
	styleUrls: ['./project-delete-popup.component.scss'],
})
export class ProjectDeletePopupComponent<
	T extends IProjectDeletePopupData
> extends DcBaseComponent {
	private readonly currentStepSubject = new BehaviorSubject<ProjectDeleteStep>(
		ProjectDeleteStep.ProjectElements
	);
	protected readonly DcIcons = DcIcons;
	protected readonly ActionIcons = ActionIcons;
	protected readonly DcLoaders = DcLoaders;
	public openedElementOverlay: string | undefined = undefined;
	public cannotDeleteProject = $localize`:i18n=@@project.delete-popup.cannot-delete:-${this.modalData.project.label}:PROJECT_LABEL:`;
	public deleteProject = $localize`:i18n=@@project.delete-popup.delete-project:-${this.modalData.project.label}:PROJECT_LABEL:`;
	// eslint-disable-next-line max-len
	public enterVerificationCode = $localize`:i18n=@@project.delete-popup.verification-message:-${this.modalData.project.label}:PROJECT_LABEL:`;
	public confirmDeletion = false;
	public deleteProjectInput = '';
	public verificationCode = '';
	public vo$!: Observable<{
		currentStep: ProjectDeleteStep;
		mode: ProjectDeletePopupMode;
		isDeleting: boolean;
	}>;
	protected readonly ProjectDeletePopupMode = ProjectDeletePopupMode;
	protected readonly ProjectDeleteStep = ProjectDeleteStep;
	@ViewChild(MatStepper)
	public stepper: MatStepper | null = null;
	public constructor(
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T,
		private readonly modalService: AppModalService,
		private readonly store: Store,
		private readonly selector: ProjectSelector
	) {
		super();
		this.cmpId = 'project-delete-popup';
		this.vo$ = combineAll({
			currentStep: this.currentStepSubject,
			isDeleting: this.selector.isDeletingInProgress$(),
		}).pipe(
			map(({ currentStep, isDeleting }) => {
				const { isDeletable, isCritical } = this.modalData.project;
				const mode = !isDeletable
					? ProjectDeletePopupMode.UndeletableMode
					: isCritical
					? ProjectDeletePopupMode.CriticalMode
					: ProjectDeletePopupMode.UncriticalMode;
				return {
					currentStep,
					mode,
					isDeleting,
				};
			})
		);
	}

	public nextStep(): void {
		if (this.stepper === null) {
			return;
		}
		this.stepper.next();
	}

	public previousStep(): void {
		if (this.stepper === null) {
			return;
		}
		this.stepper.previous();
	}

	public delete(): void {
		this.store.dispatch(
			deleteProject({
				id: this.modalData.project.id,
				verificationCode: this.verificationCode,
			})
		);
	}

	public async changeCurrentStep($event: StepperSelectionEvent): Promise<void> {
		const { isDeletable, isCritical, id } = this.modalData.project;
		if (!isDeletable || !isCritical) {
			return;
		}
		const currStep = await firstValueFrom(this.currentStepSubject);
		if (
			currStep === ProjectDeleteStep.ProjectElements &&
			$event.selectedIndex === ProjectDeleteStep.VerificationCode
		) {
			// from step1 to step2
			this.store.dispatch(
				sendVerificationCode({
					id,
				})
			);
			this.currentStepSubject.next(ProjectDeleteStep.VerificationCode);
			return;
		}
		if (
			currStep === ProjectDeleteStep.VerificationCode &&
			$event.selectedIndex === ProjectDeleteStep.ProjectElements
		) {
			// from step2 to step1
			this.currentStepSubject.next(ProjectDeleteStep.ProjectElements);
		}
	}

	public resendVerificationCode(): void {
		this.store.dispatch(
			sendVerificationCode({
				id: this.modalData.project.id,
			})
		);
	}

	public closePopup(): void {
		this.modalService.closeActiveModal();
	}
}
